import { getSearchHistory } from './../helpers'
import type { GlobalSearchQueryUI, GlobalSearchResponse, GlobalSearchResultUI } from '../global-search-models'
import { DEFAULT_GLOBAL_SEARCH_PARAMS } from '../global-search-models'
import type {
  ListboxComponent,
  ListboxItemComponent,
  PopoverComponent,
  SearchFieldComponent,
} from '@getgo/chameleon-web'
import { html, query, state } from 'lit-element'
import { ShellElement } from '../../../common/shell-element'
import { asTranslationKey } from '../../../common/translate-helpers/i18n-utils'
import { getGlobalSearchManager } from '../global-search-manager'
import globalSearch from './global-search-field.styles.scss'
import { nothing } from 'lit-html'
import debounce from 'lodash.debounce'
import type { globalSearchEvents, GlobalSearchQueryEventPayload } from '../namespace'
import { executeSearch, GLOBAL_SEARCH_NAMESPACE, notifySearchFieldQueryChange } from '../namespace'
import { getEventBus } from '../../../services/namespaces/event-bus'
import { cloneKeyboardEvent } from '../../../common/keyboard-events'
import { trackGlobalSearch } from '../analytics/analytics'

export interface ListboxEvent {
  readonly listbox: ListboxComponent | null | undefined
}

export interface GoToGlobalSearchFieldProps {
  readonly searchResults: readonly GlobalSearchResultUI[] | undefined
}

//Keys that won't trigger a new search call
const NAVIGATION_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
const KEYUP_SEARCH_KEYS_BLACKLIST = [...NAVIGATION_KEYS, 'Enter']

export class GoToGlobalSearchField extends ShellElement implements GoToGlobalSearchFieldProps {
  static readonly tagName = 'goto-global-search'

  @state() private searchString = ''
  @state() searchResults: GoToGlobalSearchFieldProps['searchResults'] | undefined
  @state() private searchHistory: readonly GlobalSearchQueryUI[] = []
  @state() private activeKey = ''
  @state() private searchError = false

  @query('chameleon-search-field') private searchField: SearchFieldComponent | undefined
  @query('chameleon-popover') private chameleonPopover: PopoverComponent | undefined

  public listbox: ListboxComponent | null | undefined
  private placeholder = asTranslationKey('Search contacts')

  private resizeObserver = new ResizeObserver(() => {
    this.updateSearchPopoverWidth()
  })

  static get styles() {
    return globalSearch
  }

  connectedCallback() {
    super.connectedCallback()
    const { searchFieldQueryChange } = getEventBus().subscribeTo<
      typeof GLOBAL_SEARCH_NAMESPACE,
      typeof globalSearchEvents
    >(GLOBAL_SEARCH_NAMESPACE)
    searchFieldQueryChange.addListener(this.queryListener)
    this.unsubscribeFunctions.push(() => {
      searchFieldQueryChange.removeListener(this.queryListener)
    })
  }

  private queryListener = (event: GlobalSearchQueryEventPayload) => {
    this.searchString = event.query.query
    if (this.searchField) {
      this.searchField.value = this.searchString
    }
    if (this.listbox) {
      this.deselectListboxItems()
    }
  }

  firstUpdated() {
    if (this.searchField) {
      this.resizeObserver.observe(this.searchField)
      this.searchField.addEventListener('paste', this.handlePasteEvent)

      this.unsubscribeFunctions.push(() => {
        this.resizeObserver.unobserve(this.searchField as Element)
        this.searchField?.removeEventListener('paste', this.handlePasteEvent)
      })
    }
  }

  handleItemClick() {
    this.closePopover()
  }

  render() {
    /**
     * When we click on the clear button and focus back on the search field this line will make sure that it doesn't display the previous search results
     * We can't use the change event from the chameleon search field since it's not working properly. Needs to be fixed on chameleon's side https://jira.ops.expertcity.com/browse/CHAMELEON-2001
     */
    this.searchString = this.searchField?.value ?? ''

    const categories = getGlobalSearchManager().getCategories()
    return categories.length
      ? html`
          <chameleon-popover data-test="global-search-popover" menu z-index="60000">
            <div slot="trigger" class="popover-trigger">
              <chameleon-search-field
                placeholder=${this.placeholder}
                @click=${this.handleClickEvent}
                @keydown=${this.handleKeydownEvent}
                @keyup=${this.handleKeyupEvent}
                @focus=${this.handleFocus}
              >
              </chameleon-search-field>
            </div>
            <div slot="content">${this.renderPopoverContent()}</div>
          </chameleon-popover>
        `
      : nothing
  }

  private renderPopoverContent() {
    if (this.searchError) {
      return html`<goto-search-error size="small"></goto-search-error>`
    } else if (this.searchString && this.searchResults) {
      return html`<goto-search-result
        @item-click=${this.handleItemClick}
        @listboxrendered=${this.handleListboxrendered}
        searchString=${this.searchString}
        .searchResults=${this.searchResults}
        ?searchError=${this.searchError}
      ></goto-search-result>`
    } else if (this.searchHistory.length && !this.searchString) {
      return html` <goto-search-history
        @item-click=${this.handleItemClick}
        @listboxrendered=${this.handleListboxrendered}
        .searchHistory=${this.searchHistory}
      ></goto-search-history>`
    } else {
      return nothing
    }
  }

  private async setSearchHistory() {
    this.searchHistory = await getSearchHistory()
  }

  private async handleFocus() {
    await this.setSearchHistory()
    // Force the popover open if a user refocuses on the field and search string is present or there is a search history
    if (this.searchString || this.searchHistory.length) {
      this.chameleonPopover?.open()
    }
  }

  private updateSearchPopoverWidth() {
    this.chameleonPopover?.setAttribute('width', Math.max(400, this.searchField?.clientWidth ?? 0).toString())
  }

  private updateSelectionState() {
    if (this.listbox) {
      const selectedIndex = this.listbox.selectedIndex
      const listboxItems = this.listbox.querySelectorAll('chameleon-listbox-item')
      if (listboxItems) {
        listboxItems.forEach((listBoxItem, index) => {
          this.updateItemSelectionState(listBoxItem, index === selectedIndex)
        })
      }
    }
  }

  /**
   * Toggle class to item to show/hide interactive sub items.
   * @param listboxItem chameleon listbox component item
   * @param selected boolean, indicates if the item is the current selected item of the listbox
   */
  private updateItemSelectionState(listboxItem: ListboxItemComponent, selected: boolean) {
    const previewItem = listboxItem.querySelector('goto-search-result-preview-item')
    const moreResultsItem = listboxItem.querySelector('chameleon-button.more-results-button')
    if (selected) {
      listboxItem.classList.add('selected')
      previewItem?.classList.add('selected')
      moreResultsItem?.classList.add('selected')
    } else {
      listboxItem.classList.remove('selected')
      previewItem?.classList.remove('selected')
      moreResultsItem?.classList.remove('selected')
    }
  }

  private setIndex(newIndex: number) {
    if (this.listbox) {
      this.listbox.selectedIndex = newIndex
      this.updateSelectionState()
    }
  }

  /**
   * A keyup event is when a pressed key on the keyboard is released
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Element/keyup_event}
   */
  private handleKeyupEvent(event: KeyboardEvent) {
    this.activeKey = event.key
    this.searchString = this.searchField?.value ?? ''
    if (!KEYUP_SEARCH_KEYS_BLACKLIST.includes(event.key)) {
      const query = { query: this.searchString }
      notifySearchFieldQueryChange({ query })
      this.searchDelay()
    }
  }

  /**
   * A keydown event is when a key on the keyboard is pressed
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Element/keydown_event}
   */
  private handleKeydownEvent(event: KeyboardEvent) {
    this.activeKey = event.key
    if (this.listbox) {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
        case ' ':
          this.dispatchEventToListboxAndPreventDefault(event)
          break

        case 'Enter':
          this.handleEnterEvent(event)
          break

        case 'ArrowUp':
          this.handleArrowUpEvent(event)
          break

        case 'ArrowDown':
          this.handleArrowDownEvent(event)
          break
      }
    }
    // Enables us to use the spacebar in the search field
    event.stopPropagation()
  }

  /**
   * Passes the event off to the Chameleon ListboxComponent
   * @see {@link https://chameleon.dev.gtc.goto.com/components/?pageid=listbox&platform=web&tab=web-component}
   */
  private dispatchEventToListbox(event: KeyboardEvent) {
    const forwardedEvent = cloneKeyboardEvent(event, { bubbles: false })
    this.listbox?.dispatchEvent(new KeyboardEvent(event.type, forwardedEvent))
  }

  /**
   * Prevents the default behavior for the event and passes it off to
   * the Chameleon ListboxComponent
   */
  private dispatchEventToListboxAndPreventDefault(event: KeyboardEvent) {
    this.dispatchEventToListbox(event)
    if (this.listboxItemsAreSelected()) {
      event.preventDefault()
    }
  }

  /**
   * When a user navigates with the `Up` key, we want to loop over each listbox item in an "upward" fashion
   */
  private handleArrowUpEvent(event: KeyboardEvent) {
    event.preventDefault()
    if (this.userIsAtStartOfListBox()) {
      //if the user is on the first item in the listbox, we deselect all items in the listbox
      this.deselectListboxItems()
    } else if (this.noListboxItemsSelected()) {
      //if the user currently has no items selected in the listbox, we bring them to the end of the listbox and select the last item in the inbox
      //thus restarting the navigation loop from the bottom up
      this.navigateToEndOfListBox()
    } else {
      //the user is navigating within the listbox component and we pass the event to the Chameleon ListboxComponent
      this.dispatchEventToListbox(event)
      this.updateSelectionState()
    }
  }

  /**
   * When a user navigates with the `Down` key, we want to loop over each listbox item in a "downward" fashion
   */
  private handleArrowDownEvent(event: KeyboardEvent) {
    event.preventDefault()
    if (this.userIsAtEndOfListbox()) {
      //if the user is on the last item in the listbox, we navigate out of the inbox
      this.deselectListboxItems()
    } else {
      //the user is navigating within the listbox component and we pass the event to the Chameleon ListboxComponent
      this.dispatchEventToListbox(event)
      this.updateSelectionState()
    }
  }

  /**
   * If the user presses enter on an item inside the listbox, we want to close the popover and perform a new search
   */
  private handleEnterEvent(event: KeyboardEvent) {
    //If user is inside the listbox
    if (this.listboxItemsAreSelected()) {
      //User is using the listbox so we pass the event to the listbox to handle and close the popover
      this.dispatchEventToListboxAndPreventDefault(event)
      this.closePopoverAndResetListbox()
    } else {
      //User is in the search bar
      //Close the popover and execute the search if string isn't empty
      if (this.searchString.trim()) {
        this.deselectListboxItems()
        this.closePopoverAndExecuteSearch()
      }
    }
  }

  private closePopoverAndResetListbox() {
    this.closePopover()
    this.deselectListboxItems()
    this.updateSelectionState()
  }

  private handleClickEvent(event: MouseEvent) {
    // Prevents the popover from opening when there is no search query or history
    event.stopPropagation()
  }

  /**
   * When the selected index of the listbox is >= 0, the user is on an item within the listbox
   */
  private listboxItemsAreSelected() {
    const selectedIndex = this.listbox?.selectedIndex ?? -1
    return selectedIndex >= 0
  }

  /**
   * When the selected index of the listbox is < 0, the user is not navigating the listbox
   */
  private noListboxItemsSelected() {
    const selectedIndex = this.listbox?.selectedIndex ?? -1
    return selectedIndex < 0
  }

  /**
   * When the selected index of the listbox = listbox.listbox - 1, the user is on the last item within the listbox
   */
  private userIsAtEndOfListbox() {
    const selectedIndex = this.listbox?.selectedIndex ?? -1
    const listboxLength = this.listbox?.length ?? 0
    return selectedIndex === listboxLength - 1
  }

  /**
   * When the selected index of the listbox is 0, the user is on the first item within the listbox
   */
  private userIsAtStartOfListBox() {
    const selectedIndex = this.listbox?.selectedIndex ?? -1
    return selectedIndex === 0
  }

  /**
   * Deselect all listbox items so that are events are handled by default again
   */
  private deselectListboxItems() {
    this.setIndex(-1)
  }

  /**
   * Bring the to the last item in the listbox
   */
  private navigateToEndOfListBox() {
    const listboxLength = this.listbox?.length ?? 0
    this.setIndex(listboxLength - 1)
  }

  private handlePasteEvent = (e: ClipboardEvent) => {
    trackGlobalSearch({
      searchString: e.clipboardData?.getData('text') ?? '',
      eventName: 'GoTo > Global Search Input Field Paste',
      eventType: 'paste',
      properties: {},
    })
  }

  private closePopover() {
    if (this.chameleonPopover?.isOpen) {
      this.chameleonPopover?.close()
    }
  }

  private triggerSearch = () => {
    this.closeOrOpenPopover()
    this.updateSearchPopoverWidth()
    if (this.searchString.trim()) {
      trackGlobalSearch({
        searchString: this.searchString,
        eventName: 'GoTo > Global Search Input Field Search Preview',
        eventType: 'keyup',
        properties: {},
      })
      const query = { query: this.searchString }
      getGlobalSearchManager()
        .searchFor(query, DEFAULT_GLOBAL_SEARCH_PARAMS)
        .then(this.handleSearchResponse, this.handleSearchError)
    }
  }
  private searchDelay = debounce(this.triggerSearch, 300)

  private handleSearchError = () => {
    this.searchError = true
  }

  private closeOrOpenPopover() {
    if (this.shouldClosePopover()) {
      this.closePopover()
    } else if (this.shouldOpenPopover()) {
      this.chameleonPopover?.open()
    }
  }

  private shouldClosePopover() {
    return (
      this.chameleonPopover && this.activeKey === 'Enter' && this.searchString.trim() && this.chameleonPopover.isOpen
    )
  }

  private shouldOpenPopover() {
    return this.chameleonPopover && !NAVIGATION_KEYS.includes(this.activeKey) && !this.chameleonPopover.isOpen
  }

  private handleSearchResponse = (response: GlobalSearchResponse | undefined) => {
    this.searchError = false
    this.searchResults = response?.items?.length
      ? getGlobalSearchManager().convertToGlobalSearchResultUI(response.items, 'preview')
      : []
  }

  private handleListboxrendered = (e: CustomEvent<ListboxEvent>) => {
    this.listbox = e.detail.listbox
  }

  private closePopoverAndExecuteSearch = () => {
    this.closePopover()
    trackGlobalSearch({
      searchString: this.searchString,
      eventName: 'GoTo > Global Search Input Field Enter',
      eventType: 'keydown',
      properties: {},
    })
    const query = { query: this.searchString }
    executeSearch(query)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToGlobalSearchField.tagName]: GoToGlobalSearchField
  }
}
